import React from 'react'
import { Box, Container, Typography, Divider } from '@mui/material'

interface HomeHighlightsProps {
    locale: string
    maxWidth?: 'lg' | 'xl' | false
    isMobile?: boolean
    domain?: string
    uspData: any
}

const HomeHighlights: React.FC<HomeHighlightsProps> = ({ maxWidth = 'xl', uspData }) => {
    const renderSection = (title: string, content: React.ReactNode, key: string | number) => (
        <Box key={key} sx={{ mb: 3 }}>
            <Typography
                component="h2"
                variant="h5"
                sx={{
                    fontWeight: 700,
                    mb: 1.5,
                    color: 'text.primary',
                    fontSize: { xs: '1.125rem', md: '1.25rem' }
                }}
            >
                {title}
            </Typography>
            <Box>{content}</Box>
            <Divider sx={{ mt: 2 }} />
        </Box>
    )

    if (!uspData) return null

    return (
        <Box sx={{ bgcolor: 'grey.50', py: { xs: 3, md: 4 } }}>
            <Container maxWidth={maxWidth} sx={{ px: { xs: 2, sm: 3 } }}>
                {/* Dynamic Sections from uspData.data */}
                {uspData.data?.map((section: any, index: number) =>
                    renderSection(
                        section.title,
                        <Box>
                            {/* Section Description */}
                            {section.description?.map((desc: string, i: number) => (
                                <Typography
                                    key={i}
                                    variant="body2"
                                    sx={{
                                        mb: 1.5,
                                        color: 'text.secondary',
                                        lineHeight: 1.6,
                                        fontSize: { xs: '0.8125rem', md: '0.875rem' }
                                    }}
                                >
                                    {desc}
                                </Typography>
                            ))}

                            {/* Section Items (nested) */}
                            {section.items && (
                                <Box component="ul" sx={{ listStyle: 'disc', pl: { xs: 2.5, md: 3 }, m: 0 }}>
                                    {section.items.map((item: any, itemIndex: number) => (
                                        <Box component="li" key={itemIndex} sx={{ mb: 2 }}>
                                            <Typography
                                                component="h3"
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: 'text.primary',
                                                    mb: 0.5,
                                                    fontSize: { xs: '0.875rem', md: '0.9375rem' }
                                                }}
                                            >
                                                {item.title}
                                            </Typography>
                                            {item.description?.map((desc: string, descIndex: number) => (
                                                <Typography
                                                    key={descIndex}
                                                    variant="body2"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        lineHeight: 1.5,
                                                        fontSize: { xs: '0.75rem', md: '0.8125rem' },
                                                        mb: 0.5
                                                    }}
                                                >
                                                    {desc}
                                                </Typography>
                                            ))}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>,
                        index
                    )
                )}

                {/* FAQ Section */}
                {uspData.faq?.items &&
                    uspData.faq.items.length > 0 &&
                    renderSection(
                        uspData.faq.title,
                        <Box>
                            {uspData.faq.items.map((faq: any, index: number) => (
                                <Box key={index} sx={{ mb: 2 }}>
                                    <Typography
                                        component="h3"
                                        variant="body1"
                                        sx={{
                                            fontWeight: 700,
                                            color: 'text.primary',
                                            mb: 0.5,
                                            fontSize: { xs: '0.875rem', md: '0.9375rem' }
                                        }}
                                    >
                                        {faq.question}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.secondary',
                                            lineHeight: 1.5,
                                            fontSize: { xs: '0.75rem', md: '0.8125rem' }
                                        }}
                                    >
                                        {faq.answer}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>,
                        'faq'
                    )}
            </Container>
        </Box>
    )
}

export default HomeHighlights